import { useEffect, useRef, useCallback } from "react";

type OscType = "sine" | "triangle" | "sawtooth" | "square";

interface DroneLayer {
  freq: number;
  gain: number;
  lfoRate: number;
  lfoDepth: number;
  type: OscType;
}

export function useAbyssAudio() {
  const ctxRef = useRef<AudioContext | null>(null);
  const masterGainRef = useRef<GainNode | null>(null);
  const oscillatorsRef = useRef<OscillatorNode[]>([]);
  const lfoNodesRef = useRef<{ lfo: OscillatorNode; gain: GainNode }[]>([]);
  const noiseSourceRef = useRef<AudioBufferSourceNode | null>(null);
  const startedRef = useRef(false);

  const buildAudio = useCallback(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const ctx = new AudioContext();
    ctxRef.current = ctx;

    // ── マスターゲイン（フェードイン用）──
    const master = ctx.createGain();
    master.gain.setValueAtTime(0, ctx.currentTime);
    master.gain.linearRampToValueAtTime(0.55, ctx.currentTime + 4.0);
    master.connect(ctx.destination);
    masterGainRef.current = master;

    // ── ドローンレイヤー定義 ──
    const layers: DroneLayer[] = [
      { freq: 40,  gain: 0.5,  lfoRate: 0.07, lfoDepth: 0.12, type: "sine"     },
      { freq: 55,  gain: 0.35, lfoRate: 0.11, lfoDepth: 0.10, type: "sine"     },
      { freq: 110, gain: 0.18, lfoRate: 0.19, lfoDepth: 0.08, type: "triangle" },
      { freq: 220, gain: 0.08, lfoRate: 0.31, lfoDepth: 0.05, type: "triangle" },
      { freq: 82,  gain: 0.12, lfoRate: 0.05, lfoDepth: 0.15, type: "sawtooth" },
    ];

    layers.forEach(({ freq, gain: gainVal, lfoRate, lfoDepth, type }) => {
      const osc = ctx.createOscillator();
      osc.type = type;
      osc.frequency.setValueAtTime(freq, ctx.currentTime);

      const layerGain = ctx.createGain();
      layerGain.gain.setValueAtTime(gainVal, ctx.currentTime);

      const lfo = ctx.createOscillator();
      lfo.type = "sine";
      lfo.frequency.setValueAtTime(lfoRate, ctx.currentTime);

      const lfoGain = ctx.createGain();
      lfoGain.gain.setValueAtTime(lfoDepth, ctx.currentTime);

      lfo.connect(lfoGain);
      lfoGain.connect(layerGain.gain);

      const filter = ctx.createBiquadFilter();
      filter.type = "lowpass";
      filter.frequency.setValueAtTime(freq * 3.5, ctx.currentTime);
      filter.Q.setValueAtTime(1.2, ctx.currentTime);

      osc.connect(filter);
      filter.connect(layerGain);
      layerGain.connect(master);

      osc.start();
      lfo.start();

      oscillatorsRef.current.push(osc);
      lfoNodesRef.current.push({ lfo, gain: lfoGain });
    });

    // ── ブラウンノイズ ──
    const bufferSize = ctx.sampleRate * 4;
    const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = noiseBuffer.getChannelData(0);
    let lastOut = 0;
    for (let i = 0; i < bufferSize; i++) {
      const white = Math.random() * 2 - 1;
      lastOut = (lastOut + 0.02 * white) / 1.02;
      data[i] = lastOut * 3.5;
    }

    const noiseSource = ctx.createBufferSource();
    noiseSource.buffer = noiseBuffer;
    noiseSource.loop = true;

    const noiseFilter = ctx.createBiquadFilter();
    noiseFilter.type = "lowpass";
    noiseFilter.frequency.setValueAtTime(180, ctx.currentTime);
    noiseFilter.Q.setValueAtTime(0.8, ctx.currentTime);

    const noiseGain = ctx.createGain();
    noiseGain.gain.setValueAtTime(0.06, ctx.currentTime);

    noiseSource.connect(noiseFilter);
    noiseFilter.connect(noiseGain);
    noiseGain.connect(master);
    noiseSource.start();
    noiseSourceRef.current = noiseSource;

    // ── 定期的な「うめき」音 ──
    const scheduleGroan = () => {
      if (!ctxRef.current || ctxRef.current.state === "closed") return;
      const delay = 8 + Math.random() * 14;

      const groanOsc = ctx.createOscillator();
      groanOsc.type = "sine";
      groanOsc.frequency.setValueAtTime(28 + Math.random() * 20, ctx.currentTime + delay);
      groanOsc.frequency.linearRampToValueAtTime(18, ctx.currentTime + delay + 3);

      const groanGain = ctx.createGain();
      groanGain.gain.setValueAtTime(0, ctx.currentTime + delay);
      groanGain.gain.linearRampToValueAtTime(0.3, ctx.currentTime + delay + 0.8);
      groanGain.gain.linearRampToValueAtTime(0, ctx.currentTime + delay + 3.5);

      groanOsc.connect(groanGain);
      groanGain.connect(master);
      groanOsc.start(ctx.currentTime + delay);
      groanOsc.stop(ctx.currentTime + delay + 4);
      groanOsc.onended = scheduleGroan;
    };
    scheduleGroan();
  }, []);

  // ── グリッチ音 ──
  const triggerGlitchSound = useCallback(() => {
    const ctx = ctxRef.current;
    if (!ctx || ctx.state === "closed") return;

    const baseFreqs = [40, 55, 110, 220, 82];
    oscillatorsRef.current.forEach((osc, i) => {
      const base = baseFreqs[i] ?? 40;
      const glitchFreq = base * (1.5 + Math.random() * 2);
      osc.frequency.setValueAtTime(glitchFreq, ctx.currentTime);
      osc.frequency.linearRampToValueAtTime(base, ctx.currentTime + 0.4 + Math.random() * 0.6);
    });

    const burstBuffer = ctx.createBuffer(1, Math.floor(ctx.sampleRate * 0.15), ctx.sampleRate);
    const burstData = burstBuffer.getChannelData(0);
    for (let i = 0; i < burstData.length; i++) {
      burstData[i] = (Math.random() * 2 - 1) * (1 - i / burstData.length);
    }
    const burst = ctx.createBufferSource();
    burst.buffer = burstBuffer;

    const burstFilter = ctx.createBiquadFilter();
    burstFilter.type = "bandpass";
    burstFilter.frequency.setValueAtTime(800, ctx.currentTime);
    burstFilter.Q.setValueAtTime(0.5, ctx.currentTime);

    const burstGain = ctx.createGain();
    burstGain.gain.setValueAtTime(0.08, ctx.currentTime);

    burst.connect(burstFilter);
    burstFilter.connect(burstGain);
    burstGain.connect(masterGainRef.current ?? ctx.destination);
    burst.start();
  }, []);

  // ── フェードアウト & クリーンアップ ──
  const stopAudio = useCallback((fadeTime = 1.5) => {
    const ctx = ctxRef.current;
    const master = masterGainRef.current;
    if (!ctx || !master) return;

    master.gain.linearRampToValueAtTime(0, ctx.currentTime + fadeTime);

    setTimeout(() => {
      oscillatorsRef.current.forEach((osc) => {
        try { osc.stop(); } catch (e) { void e; }
      });
      lfoNodesRef.current.forEach(({ lfo }) => {
        try { lfo.stop(); } catch (e) { void e; }
      });
      try { noiseSourceRef.current?.stop(); } catch (e) { void e; }
      try { ctx.close(); } catch (e) { void e; }
      oscillatorsRef.current = [];
      lfoNodesRef.current = [];
      startedRef.current = false;
    }, (fadeTime + 0.2) * 1000);
  }, []);

  useEffect(() => {
    return () => { stopAudio(0.5); };
  }, [stopAudio]);

  return { buildAudio, triggerGlitchSound, stopAudio };
}
